import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { WellnessCheckInResultsEssentialsArticleContent } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/types'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { BulletIcon } from '../../atoms/icons/BulletIcon'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { Image } from '../../atoms/image/Image'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { useVerticalOverflow } from '../../hooks/useVerticalOverflow/useVerticalOverflow'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const DEFAULT_DESCRIPTION_NUMBER_OF_LINES = 1

export interface WellnessCheckInResultsEssentialsCardProps {
  articleContent: WellnessCheckInResultsEssentialsArticleContent
  onArticlePress?: (subPath: string) => void
  descriptionNumberOfLines?: number
  titleNumberOfLines?: number
  typeTextNumberOfLines?: number
  truncateDescriptionOnOverflow?: boolean
  customStyles?: {
    mainContainer?: CSSObject
    imageContainer?: CSSObject
    textContainer?: CSSObject
  }
  onCardPress?: () => void
  testID?: string
}

const MainContainer = styled(PressableOpacity)<{ theme: ThemeType; customStyling?: CSSObject }>(
  ({ theme, customStyling = {} }) => ({
    padding: `${theme.spacing['12px']}`,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.components.wellnessCheckInResultsEssentialsCard.background,
    borderRadius: theme.spacing['12px'],
    width: '100%',
    minWidth: '343px',
    ...customStyling,
  }),
)

const TextContainer = styled.View<{ theme: ThemeType; customStyling?: CSSObject }>(({ theme, customStyling = {} }) => ({
  padding: `0 ${theme.spacing['16px']} 0 ${theme.spacing['12px']}`,
  flex: 1,
  ...customStyling,
}))

const DescriptionContainer = styled(BodyText)({
  paddingBottom: `6px`,
})

const TypeAndDurationContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const TypeContainer = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  paddingRight: theme.spacing['8px'],
}))

const AttributeContainer = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  paddingLeft: theme.spacing['8px'],
}))

const ImageContainer = styled(Image)<{ theme: ThemeType; customStyling?: CSSObject }>(
  ({ theme, customStyling = {} }) => ({
    width: '64px',
    height: '64px',
    borderRadius: theme.spacing['4px'],
    ...customStyling,
  }),
)

export const WellnessCheckInResultsEssentialsCard: FunctionComponent<WellnessCheckInResultsEssentialsCardProps> = ({
  articleContent,
  onArticlePress,
  descriptionNumberOfLines = DEFAULT_DESCRIPTION_NUMBER_OF_LINES,
  titleNumberOfLines,
  typeTextNumberOfLines,
  truncateDescriptionOnOverflow = false,
  customStyles,
  onCardPress,
  testID = 'WellnessCheckInResultsEssentialsCard',
}) => {
  const { title, description, type, attribute, subPath, imageUrl, attributeDescValues } = articleContent
  const { colors } = useTheme() as ThemeType
  const { breakpoints } = useTheme()
  const { formatMessage } = useIntl()

  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const [initialOverflowCheck, setInitialOverflowCheck] = useState(false)
  const { isVerticallyOverflowing, checkVerticalOverflow } = useVerticalOverflow({
    webRefs: IS_WEB ? { containerRef, contentRef } : undefined,
  })
  // if content is overflowing, only show 1 line of description
  const descriptionLines = useMemo(
    () => (truncateDescriptionOnOverflow && isVerticallyOverflowing ? 1 : descriptionNumberOfLines),
    [descriptionNumberOfLines, isVerticallyOverflowing, truncateDescriptionOnOverflow],
  )

  useEffect(() => {
    if (IS_WEB && truncateDescriptionOnOverflow && !initialOverflowCheck) {
      // Needs delay to ensure accurate height measurement after all content (including nested components,
      // images, and text wrapping) has been fully rendered and laid out. This is particularly important
      // for cards that are not in the first position in the WellnessCheckInResultsEssentialsCarousel.
      const timeoutId = setTimeout(() => {
        checkVerticalOverflow()
        setInitialOverflowCheck(true)
      }, 500)
      return () => clearTimeout(timeoutId)
    }
    return
  }, [checkVerticalOverflow, initialOverflowCheck, truncateDescriptionOnOverflow])

  return (
    <MainContainer
      testID={tID(testID)}
      onPress={() => {
        onCardPress?.()
        onArticlePress && subPath && onArticlePress(subPath)
      }}
      customStyling={customStyles?.mainContainer}
      pressableRef={containerRef}
    >
      <ImageContainer source={imageUrl} customStyling={customStyles?.imageContainer} accessibilityIgnoresInvertColors />
      <TextContainer ref={contentRef} customStyling={customStyles?.textContainer}>
        <View>
          {breakpoints.isMobileSized ? (
            <BodyText text={formatMessage(title)} size={BodyTextSize.SMALL} numberOfLines={titleNumberOfLines} bold />
          ) : (
            <Title text={formatMessage(title)} size={TitleSize.XSMALL} numberOfLines={titleNumberOfLines} />
          )}
          <DescriptionContainer
            text={formatMessage(description)}
            numberOfLines={descriptionLines}
            size={breakpoints.isMobileSized ? BodyTextSize.CAPTION : BodyTextSize.SMALL}
            color={colors.textSecondary}
          />
        </View>
        {(type || attribute) && (
          <TypeAndDurationContainer>
            {type && (
              <TypeContainer
                text={formatMessage(type)}
                size={BodyTextSize.CAPTION}
                color={colors.textSecondary}
                numberOfLines={typeTextNumberOfLines}
              />
            )}
            {attribute && (
              <>
                <BulletIcon fillColor={colors.textSecondary} size={3} />
                <AttributeContainer
                  text={formatMessage(attribute, attributeDescValues)}
                  size={BodyTextSize.CAPTION}
                  color={colors.textSecondary}
                />
              </>
            )}
          </TypeAndDurationContainer>
        )}
      </TextContainer>
      <ChevronIcon direction={ChevronIconDirection.RIGHT} fillColor={colors.iconDefault} size={24} />
    </MainContainer>
  )
}
